import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, GraduationCap, Sparkles, Timer, TrendingUp, School, Award, Target, BookOpen, CheckCircle2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { api, V1 } from "@/lib/api";
import RecentArticlesWidget from "@/components/RecentArticlesWidget";

const HERO_IMG = "https://images.pexels.com/photos/7092523/pexels-photo-7092523.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Home() {
  const [schools, setSchools] = useState([]);
  const [exams, setExams] = useState([]);
  const [mockExams, setMockExams] = useState([]);
  const [stats, setStats] = useState({ schools: 0, exams: 0, mocks: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [sRes, eRes, mRes, statRes] = await Promise.all([
          api.get(`${V1}/schools?featured=true&limit=6`),
          api.get(`${V1}/scholarship-exams?status=open&limit=4`),
          api.get(`${V1}/mock-exams?upcoming=true`),
          api.get(`${V1}/schools/public/stats`)
        ]);
        setSchools(sRes.data.items || []);
        setExams(eRes.data || []);
        setMockExams(mRes.data || []);
        if (statRes.data) {
          setStats({
            schools: statRes.data.schools || 0,
            exams: statRes.data.bursluluk || 0,
            mocks: statRes.data.exams || 0,
          });
        }
      } catch (e) { /* ignore */ }
    })();
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Badge variant="secondary" className="mb-6 bg-accent/10 text-accent border border-accent/20" data-testid="hero-badge">
                <Sparkles className="h-3 w-3 mr-1" /> Türkiye'nin bursluluk sınavı platformu
              </Badge>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95] tracking-tight mb-6">
                Özel okul <span className="hero-gradient-text">bursluluk sınavlarını</span> bul, başvur, kazan.
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed mb-8">
                6 ilden özel okulların bursluluk fırsatları, haftalık deneme sınavları, LGS/TYT/AYT çıkmış sorular ve
                yapay zekâ destekli tercih rehberliği tek yerde.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 btn-lift" data-testid="hero-cta-schools">
                  <Link to="/okullar">Bursluluk Sınavı Bul <ArrowRight className="h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" data-testid="hero-cta-mock">
                  <Link to="/deneme-sinavlari">Deneme Sınavına Katıl</Link>
                </Button>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-8 max-w-lg">
                <Stat n={stats.schools} l="Kayıtlı Okul" />
                <Stat n={stats.exams} l="Açık Bursluluk" />
                <Stat n={stats.mocks} l="Deneme Sınavı" />
              </div>
            </motion.div>
          </div>
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-border brutal-shadow"
            >
              <img src={HERO_IMG} alt="Öğrenciler sınav çözüyor" className="h-full w-full object-cover" />
              <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-background/95 backdrop-blur border border-border p-4">
                <div className="overline text-accent mb-1">Öne çıkan</div>
                <div className="font-display font-bold text-lg leading-tight">Haftalık LGS Deneme Sınavı</div>
                <div className="text-xs text-muted-foreground mt-1">Her Cumartesi 10:00 · Ücretsiz</div>
                <Button asChild size="sm" className="mt-3 w-full" data-testid="hero-featured-cta">
                  <Link to="/deneme-sinavlari">Katılacağım</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURED SCHOOLS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div>
            <div className="overline text-accent mb-2">Öne Çıkan Okullar</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              Bursluluk sınavı açan okullar
            </h2>
          </div>
          <Button asChild variant="outline" data-testid="see-all-schools">
            <Link to="/okullar">Tüm Okullar <ArrowRight className="h-4 w-4" /></Link>
          </Button>
        </div>
        <Carousel className="w-full relative px-12">
          <CarouselContent className="-ml-4">
            {schools.map((s) => (
              <CarouselItem key={s.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <Link
                  to={`/okul/${s.slug}`}
                  data-testid={`home-school-card-${s.slug}`}
                  className="block group relative overflow-hidden rounded-xl border border-border bg-card transition-all hover:border-primary/40 hover:-translate-y-1 h-full"
                  style={{ transition: "transform 0.2s ease, border-color 0.2s ease" }}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-muted">
                    <img src={s.cover_url} alt={s.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5 flex flex-col justify-between h-[calc(100%-75%)]">
                    <div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                        <MapPin className="h-3 w-3" /> {s.province} / {s.district}
                      </div>
                      <div className="font-display font-bold text-lg leading-tight mb-2">{s.name}</div>
                      <div className="text-sm text-muted-foreground line-clamp-2">{s.short_intro}</div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-1">
                      {(s.education_levels || []).slice(0, 3).map((l) => (
                        <span key={l} className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-muted rounded">{l}</span>
                      ))}
                    </div>
                  </div>
                </Link>
              </CarouselItem>
            ))}
          </CarouselContent>
          {schools.length > 1 && (
            <>
              <CarouselPrevious className="absolute -left-2 top-1/2 -translate-y-1/2" />
              <CarouselNext className="absolute -right-2 top-1/2 -translate-y-1/2" />
            </>
          )}
        </Carousel>
      </section>

      {/* UPCOMING EXAMS */}
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-3 gap-10">
            <div>
              <div className="overline text-accent mb-4">Yaklaşan Bursluluk Sınavları</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Fırsatı kaçırma, hemen başvur.
              </h2>
              <p className="text-primary-foreground/70 leading-relaxed">
                Onlarca özel okulun bursluluk sınavına tek panelden başvur, sonuçları anlık takip et.
              </p>
              <Button asChild variant="secondary" size="lg" className="mt-6 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="upcoming-cta">
                <Link to="/bursluluk-sinavlari">Tüm Sınavlar</Link>
              </Button>
            </div>
            <div className="lg:col-span-2 grid gap-3">
              {exams.slice(0, 4).map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-4 p-4 rounded-lg border border-primary-foreground/10 bg-primary-foreground/5" data-testid={`upcoming-exam-${e.id}`}>
                  <div>
                    <div className="text-xs text-primary-foreground/60">{e.school?.province} / {e.school?.district}</div>
                    <div className="font-display font-semibold">{e.title}</div>
                    <div className="text-xs mt-1 text-primary-foreground/70">
                      Sınav: {new Date(e.exam_date).toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })}
                    </div>
                  </div>
                  <Button asChild size="sm" variant="secondary" className="bg-accent text-accent-foreground hover:bg-accent/90">
                    <Link to={`/okul/${e.school?.slug || ""}`}>Detay</Link>
                  </Button>
                </div>
              ))}
              {exams.length === 0 && <div className="text-primary-foreground/60 text-sm">Aktif bursluluk sınavı yok.</div>}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-3xl mb-12">
          <div className="overline text-accent mb-3">Nasıl Çalışır</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            4 basit adımda bursluluk yolculuğu.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <div key={s.title} className="p-6 rounded-xl border border-border bg-card">
              <div className="text-5xl font-display font-black text-accent/20 mb-3">0{i + 1}</div>
              <s.icon className="h-6 w-6 text-primary mb-3" />
              <div className="font-display font-bold text-lg mb-1">{s.title}</div>
              <div className="text-sm text-muted-foreground leading-relaxed">{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* MOCK EXAMS + FEATURES bento */}
      <section className="bg-muted/40 border-y border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid md:grid-cols-8 lg:grid-cols-12 gap-6">
          <Card className="md:col-span-5 lg:col-span-7 border-border p-8 relative overflow-hidden">
            <div className="overline text-accent mb-2">Haftalık & Aylık</div>
            <h3 className="font-display text-2xl md:text-3xl font-bold mb-3">Online Deneme Sınavları</h3>
            <p className="text-muted-foreground max-w-xl mb-6">
              LGS, TYT ve AYT formatına uygun, sunucu saatiyle kontrol edilen, güvenli çevrimiçi sınav altyapısı.
              Katılım tutarlılığın okullar tarafından anonim olarak görülebilir.
            </p>
            <div className="grid gap-3">
              {mockExams.slice(0, 3).map((m) => (
                <div key={m.id} className="flex items-center justify-between p-3 border border-border rounded-lg bg-background">
                  <div>
                    <div className="text-xs text-muted-foreground">{m.exam_type} · {m.duration_minutes} dk · {m.question_count} soru</div>
                    <div className="font-medium">{m.title}</div>
                  </div>
                  <Button asChild size="sm" variant="outline"><Link to="/deneme-sinavlari">Katıl</Link></Button>
                </div>
              ))}
            </div>
          </Card>
          <div className="md:col-span-3 lg:col-span-5 grid gap-6">
            <Card className="p-6 border-border">
              <TrendingUp className="h-6 w-6 text-accent mb-3" />
              <div className="font-display font-bold text-lg mb-1">Performans Analitiği</div>
              <div className="text-sm text-muted-foreground">Ders bazlı ortalama, gelişim grafiği, katılım tutarlılığı ve zaman yönetimi.</div>
            </Card>
            <Card className="p-6 border-border">
              <Sparkles className="h-6 w-6 text-accent mb-3" />
              <div className="font-display font-bold text-lg mb-1">AI Tercih Danışmanlığı</div>
              <div className="text-sm text-muted-foreground">Puanına göre "zorlayıcı, gerçekçi ve güvenli" öneriler.</div>
            </Card>
            <Card className="p-6 border-border">
              <Award className="h-6 w-6 text-accent mb-3" />
              <div className="font-display font-bold text-lg mb-1">Okul Davetleri</div>
              <div className="text-sm text-muted-foreground">Başarılı öğrenciler okullardan tam/kısmi burs teklifi alır.</div>
            </Card>
          </div>
        </div>
      </section>

      <RecentArticlesWidget />

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="rounded-2xl border border-border bg-card p-8 md:p-14 grid md:grid-cols-2 gap-8 items-center brutal-shadow">
          <div>
            <div className="overline text-accent mb-3">Aileler İçin</div>
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-3">
              Bir hesapla tüm çocuklarını takip et.
            </h3>
            <p className="text-muted-foreground mb-6">
              SMS doğrulama ile hızlı kayıt, çoklu çocuk profili, tek panelden başvuru ve tüm bildirimler.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" data-testid="cta-register-parent">
                <Link to="/kayit">Veli Kaydı Aç</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/okullar-icin">Okullar İçin</Link>
              </Button>
            </div>
          </div>
          <ul className="grid gap-3">
            {benefits.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground shrink-0">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <span className="text-sm">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}

const Stat = ({ n, l }) => (
  <div>
    <div className="font-display text-3xl font-bold">{n}</div>
    <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">{l}</div>
  </div>
);

const steps = [
  { icon: School, title: "Okul Seç", desc: "İl, ilçe ve seviyeye göre filtreleyip özel okulları keşfedin." },
  { icon: BookOpen, title: "Başvuru Yap", desc: "Bir hesapla tüm çocuklarınız için bursluluk başvurusu yapın." },
  { icon: Timer, title: "Sınava Katıl", desc: "Sunucu saatiyle çalışan güvenli online sınav altyapısıyla katılın." },
  { icon: Target, title: "Sonuç ve Tercih", desc: "Sonuçları, davetleri ve AI destekli tercih önerilerini görün." },
];

const benefits = [
  "SMS doğrulamalı güvenli veli hesabı",
  "Tek hesaptan sınırsız çocuk profili",
  "Bursluluk başvurusu ve durum takibi",
  "Haftalık ve aylık deneme sınavları",
  "LGS · TYT · AYT çıkmış sorular arşivi",
  "Yapay zekâ destekli tercih önerileri",
];
